import { assertNever } from 'src/games/common/helper';
import { BehaviorSubject, Subject } from 'rxjs';
import { BlackjackAssetType, BlackjackSpriteType } from '../../model/blackjack-types';
import { Card, CardSuit, PileId } from 'game-logic';
import { GameServices } from '../../services/game-services';

export class CardViewModel {
  public get cardBackAsset() {
    return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardBack);
  }
  public get cardFrontAsset() {
    if (this.card.cardId === undefined) {
      return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardHeart);
    }
    switch (this.card.cardId.cardSuit) {
      case CardSuit.Club:
        return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardClub);
      case CardSuit.Spade:
        return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardSpade);
      case CardSuit.Heart:
        return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardHeart);
      case CardSuit.Diamond:
        return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardDiamond);
      default:
        assertNever(this.card.cardId.cardSuit, 'Invalid card suit');
    }
  }
  public get cardShadowAsset() {
    return this.gameServices.assetCatalogueService.getAsset(BlackjackAssetType.CardShadow);
  }
  public get cardNumberLargeAsset() {
    if (this.card.cardId === undefined) {
      return this.gameServices.assetCatalogueService.getSprite(
        BlackjackSpriteType.NumbersLargePink,
      );
    }
    return this.gameServices.assetCatalogueService.getSprite(
      this.card.cardId.isRed
        ? BlackjackSpriteType.NumbersLargePink
        : BlackjackSpriteType.NumbersLargeBlack,
    );
  }
  public get cardNumberSmallAsset() {
    if (this.card.cardId === undefined) {
      return this.gameServices.assetCatalogueService.getSprite(
        BlackjackSpriteType.NumbersSmallPink,
      );
    }
    return this.gameServices.assetCatalogueService.getSprite(
      this.card.cardId.isRed
        ? BlackjackSpriteType.NumbersSmallPink
        : BlackjackSpriteType.NumbersSmallBlack,
    );
  }
  public get cardIndex() {
    return this.card.index;
  }
  public get cardNumber() {
    if (this.card.cardId === undefined) {
      return 0;
    }
    return this.card.cardId.cardType - 1;
  }
  public get isFaceUp() {
    return this.card.isFaceUp;
  }
  public get isMoveCompleted() {
    return this.isMoveComplete;
  }
  public get showShadow$() {
    return this.showShadow.asObservable();
  }
  public get currentDepth$() {
    return this.depth.asObservable();
  }
  public get refreshCard$() {
    return this.refreshCard.asObservable();
  }

  private readonly gameServices: GameServices;
  private card: Card;
  private stackIndex: number;
  private pileId: PileId;
  private localFaceUp: boolean;
  private isMoveComplete: boolean;
  private readonly showShadow: BehaviorSubject<boolean>;
  private readonly depth: BehaviorSubject<number>;
  private readonly refreshCard: Subject<void>;

  constructor(gameServices: GameServices, card: Card, stackIndex: number, pileId: PileId) {
    this.gameServices = gameServices;
    this.card = card;
    this.stackIndex = stackIndex;
    this.pileId = pileId;
    this.localFaceUp = false;
    this.isMoveComplete = true;
    this.showShadow = new BehaviorSubject<boolean>(true);
    this.depth = new BehaviorSubject<number>(stackIndex);
    this.refreshCard = new Subject<void>();
  }

  public refresh() {
    this.refreshCard.next();
  }

  public enableShadow(enable: boolean): void {
    this.showShadow.next(enable);
  }

  public setDepth(depth: number): void {
    this.depth.next(depth);
  }
}
